import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Activity,
  AlertCircle,
  BookOpen,
  Calendar,
  ChevronRight,
  Coffee,
  Flame,
  Heart,
  MapPin,
  MoreHorizontal,
  Plane,
  Plus,
  Search,
  Share2,
  Users
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { toDateString } from '../utils/eventDateFilters';
import { getThumbUrl } from '../utils/travelImages';
import { TRAVEL_BUDGET_LABELS } from '../utils/travelPostLabels';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { SECTION_THEME } from '../utils/sectionTheme';
import BannerScene from '../components/common/BannerScene';
import { EVENT_CATEGORIES, SPORT_CATEGORY } from '../utils/eventCategories';
import { TAIWAN_CITIES } from '../utils/taiwanCities';
import { compareTrendingEvents } from '../utils/eventDiscovery';
import ShareCardModal from '../components/common/ShareCardModal';
import QuizBanner from '../components/home/QuizBanner';
import PersonalFootprintWidget from '../components/home/PersonalFootprintWidget';
import ActiveOrganizersBoard from '../components/home/ActiveOrganizersBoard';
import { UserContext } from '../App';

const selectStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '13px 16px',
  color: '#334155',
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: '700',
  cursor: 'pointer'
};

const dateInputStyle = {
  boxSizing: 'border-box',
  minHeight: '46px',
  padding: '13px 12px',
  color: '#334155',
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
  fontFamily: 'inherit',
  fontSize: '14px'
};

const CATEGORY_STYLE = {
  美食: { icon: Coffee, color: '#f59e0b', background: '#fff7e8' },
  運動: { icon: Activity, color: '#16a34a', background: '#ecfdf3' },
  旅遊: { icon: Plane, color: '#4e8bca', background: '#eef4f9' },
  文藝: { icon: Heart, color: '#db7093', background: '#fdf2f8' },
  聚會: { icon: Users, color: '#8b5cf6', background: '#f5f3ff' },
  戶外: { icon: MapPin, color: '#0f9f92', background: '#ecfdfb' },
  學習: { icon: BookOpen, color: '#6366f1', background: '#eef2ff' },
  其他: { icon: MoreHorizontal, color: '#64748b', background: '#f1f5f9' }
};

const BUDGET_STYLE = {
  backpacker: { color: '#047857', background: '#ecfdf5' },
  budget: { color: '#376ba1', background: '#eef4f9' },
  standard: { color: '#7c3aed', background: '#f5f3ff' },
  premium: { color: '#d97706', background: '#fffbeb' },
  low: { color: '#047857', background: '#ecfdf5' },
  mid: { color: '#376ba1', background: '#eef4f9' },
  high: { color: '#d97706', background: '#fffbeb' }
};

const formatDateRange = (start, end) => {
  if (!start) return '日期未定';

  const startDate = new Date(start).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  if (!end) return startDate;

  const endDate = new Date(end).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  return `${startDate}－${endDate}`;
};

const formatEventWhen = (dateString, startTime) => {
  if (!dateString) return '日期未定';

  const eventDate = new Date(`${dateString}T00:00:00`);
  const todayString = toDateString(new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const time = (startTime || '').slice(0, 5);

  let dayLabel;
  if (dateString === todayString) dayLabel = '今天';
  else if (dateString === toDateString(tomorrow)) dayLabel = '明天';
  else dayLabel = eventDate.toLocaleDateString('zh-TW', { weekday: 'short' });

  return time ? `${dayLabel} ${time}` : dayLabel;
};

const handleImageError = (event, originalUrl) => {
  const image = event.currentTarget;
  if (!image.dataset.originalAttempted && originalUrl && image.src !== originalUrl) {
    image.dataset.originalAttempted = 'true';
    image.src = originalUrl;
    return;
  }

  image.style.display = 'none';
};

export default function Home() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { currentUser } = React.useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [travelPicks, setTravelPicks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [trendingError, setTrendingError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [shareEvent, setShareEvent] = useState(null);

  const loadHomeData = useCallback(async () => {
    setIsLoading(true);
    try {
      const todayString = toDateString(new Date());
      const [eventsResult, travelResult, trendingResult] = await Promise.all([
        supabase
          .from('events')
          .select('id, title, date, start_time, location, city, category, image_url, max_participants, event_participants(status)')
          .gte('date', todayString)
          .order('date', { ascending: true })
          .limit(50),
        supabase
          .from('travel_posts')
          .select('id, destination, countries, country, trip_start_date, trip_end_date, budget_range, max_companions, gender_pref, image_urls, organizer_id, profiles:organizer_id(id, name, avatar)')
          .eq('status', 'open')
          .order('created_at', { ascending: false })
          .limit(6),
        supabase
          .from('event_recent_trending_scores')
          .select('event_id, recent_approved_count, trending_score')
          .limit(50)
      ]);

      if (eventsResult.error) throw eventsResult.error;
      if (travelResult.error) throw travelResult.error;

      const trendingScores = new Map((trendingResult.data || []).map(score => [score.event_id, score]));
      const eventsWithCounts = (eventsResult.data || []).map(event => {
        const trending = trendingScores.get(event.id);
        return {
          ...event,
          approvedCount: (event.event_participants || []).filter(participant => participant.status === 'approved').length,
          isTrending: Boolean(trending),
          recentApprovedCount: Number(trending?.recent_approved_count || 0),
          trendingScore: Number(trending?.trending_score || 0)
        };
      });

      if (trendingResult.error) {
        console.error('載入近期熱門失敗:', trendingResult.error);
        setTrendingError('近期熱門暫時無法載入，其他內容仍可正常瀏覽。');
      } else {
        setTrendingError('');
      }

      setUpcomingEvents(eventsWithCounts);
      setTravelPicks(travelResult.data || []);
    } catch (error) {
      console.error('載入首頁資料失敗:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const popularEvents = useMemo(() => upcomingEvents
    .filter(event => (
      event.isTrending &&
      (!categoryFilter || event.category === categoryFilter) &&
      (!cityFilter || event.city === cityFilter) &&
      (!timeStart || event.date >= timeStart) &&
      (!timeEnd || event.date <= timeEnd)
    ))
    .sort(compareTrendingEvents)
    .slice(0, 4), [upcomingEvents, categoryFilter, cityFilter, timeStart, timeEnd]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const keyword = searchQuery.trim();
    navigate(keyword ? `/events?q=${encodeURIComponent(keyword)}` : '/events');
  };

  const searchForm = (
    <form onSubmit={handleSearchSubmit} role="search" style={{ position: 'relative', marginBottom: isDesktop ? 0 : '28px' }}>
      <Search
        aria-hidden="true"
        size={19}
        strokeWidth={2}
        style={{
          position: 'absolute',
          top: '50%',
          left: '16px',
          zIndex: 1,
          color: '#94a3b8',
          transform: 'translateY(-50%)',
          pointerEvents: 'none'
        }}
      />
      <input
        aria-label="搜尋目的地、活動或運動"
        type="search"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        onFocus={(event) => {
          event.currentTarget.style.borderColor = '#9cc2e6';
          event.currentTarget.style.boxShadow = '0 0 0 4px rgba(78, 139, 202, 0.11), 0 8px 24px rgba(15, 23, 42, 0.06)';
        }}
        onBlur={(event) => {
          event.currentTarget.style.borderColor = '#e2e8f0';
          event.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 23, 42, 0.05)';
        }}
        placeholder="搜尋目的地、活動、運動..."
        style={{
          boxSizing: 'border-box',
          width: '100%',
          minHeight: '50px',
          padding: '13px 16px 13px 46px',
          color: '#0f172a',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          outline: 'none',
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
          fontFamily: 'inherit',
          fontSize: '15px',
          transition: 'border-color 0.2s, box-shadow 0.2s'
        }}
      />
    </form>
  );

  const desktopFilterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '25px' }}>
      <select aria-label="篩選內容類型或活動類別" value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)} style={selectStyle}>
        <option value="">全部內容</option>
        <option value={SPORT_CATEGORY}>揪運動</option>
        <optgroup label="揪活動類別">
          {EVENT_CATEGORIES.map(category => <option key={category} value={category}>{category}</option>)}
        </optgroup>
      </select>
      <select aria-label="篩選地區" value={cityFilter} onChange={event => setCityFilter(event.target.value)} style={selectStyle}>
        <option value="">全部地區</option>
        {TAIWAN_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
      </select>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input type="date" aria-label="篩選開始日期" value={timeStart} max={timeEnd || undefined} onChange={event => setTimeStart(event.target.value)} style={dateInputStyle} />
        <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
        <input type="date" aria-label="篩選結束日期" value={timeEnd} min={timeStart || undefined} onChange={event => setTimeEnd(event.target.value)} style={dateInputStyle} />
      </div>
    </div>
  );

  return (
    <div style={{
      width: '100%',
      minHeight: 'calc(100vh - 60px)',
      overflowX: 'clip',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fbfd 28%, #f5f8fb 100%)',
      padding: isDesktop ? '0 0 40px' : '20px 0 calc(108px + env(safe-area-inset-bottom))'
    }}>
      <Helmet>
        <title>JoinU 揪遊 | 今天，想一起做什麼？</title>
        <meta name="description" content="探索活動、旅伴與運動，遇見和你一拍即合的人。告別無聊的滑動配對，用一場活動開啟真實連結！" />
        <meta property="og:title" content="JoinU 揪遊 | 今天，想一起做什麼？" />
        <meta property="og:description" content="探索活動、旅伴與運動，遇見和你一拍即合的人。告別無聊的滑動配對，用一場活動開啟真實連結！" />
        <meta property="og:type" content="website" />
        {/* TODO: Add a generic OG image URL here */}
      </Helmet>

      {isDesktop && (
        <header style={{ position: 'relative', overflow: 'hidden', minHeight: '176px', padding: '20px 32px 22px', background: SECTION_THEME.home.bannerBase, borderRadius: '0 0 32px 32px' }}>
          <BannerScene src={SECTION_THEME.home.bannerImage} alt="首頁企鵝吉祥物" />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '1360px', margin: '0 auto' }}>
            <div style={{ maxWidth: '58%' }}>
              <h1
                id="home-greeting"
                style={{
                  margin: '0 0 6px',
                  fontSize: '1.875rem',
                  fontWeight: '850',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.35'
                }}
              >
                <span style={{ color: '#1e2f42' }}>今天，</span>
                <span style={{ color: SECTION_THEME.home.accent }}>想一起做什麼？</span>
              </h1>
              <p style={{ margin: '0 0 18px', color: '#4c6d8c', fontSize: '0.875rem', lineHeight: '1.4' }}>
                探索活動、旅伴與運動，遇見和你一拍即合的人
              </p>
              {searchForm}
            </div>
          </div>
        </header>
      )}

      <div style={{ width: '100%', maxWidth: isDesktop ? '1360px' : '780px', margin: '0 auto', padding: isDesktop ? '25px 32px 0' : '0 18px' }}>
        {!isDesktop && (
          <section aria-labelledby="home-greeting">
            <h1
              id="home-greeting"
              style={{
                margin: '0 0 4px',
                color: '#0f172a',
                fontSize: 'clamp(1.25rem, 5vw, 1.5rem)',
                fontWeight: '800',
                letterSpacing: '-0.035em',
                lineHeight: '1.35'
              }}
            >
              今天，想一起做什麼？
            </h1>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
              探索活動、旅伴與運動，遇見和你一拍即合的人
            </p>
            {searchForm}
          </section>
        )}

        {isDesktop && desktopFilterBar}

        <div style={{ marginTop: '24px' }}>
          {currentUser ? <PersonalFootprintWidget /> : <ActiveOrganizersBoard />}
          <QuizBanner />
        </div>

        <section aria-labelledby="travel-picks-title">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '13px' }}>
            <h2 id="travel-picks-title" style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '800', letterSpacing: '-0.02em' }}>
              為你推薦
            </h2>
            <button
              type="button"
              onClick={() => navigate('/travel')}
              style={{ padding: '5px 0', color: '#376ba1', background: 'transparent', border: 'none', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
            >
              查看全部
            </button>
          </div>

          {isLoading && (
            <div style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}>
              {[0, 1, 2].map(item => (
                <div
                  key={item}
                  aria-hidden="true"
                  style={{
                    flex: '0 0 clamp(168px, 46vw, 218px)',
                    height: '220px',
                    borderRadius: '18px',
                    background: 'linear-gradient(110deg, #edf2f7 8%, #f8fafc 18%, #edf2f7 33%)',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)'
                  }}
                />
              ))}
            </div>
          )}

          {!isLoading && travelPicks.length === 0 && (
            <div style={{ padding: '30px 20px', color: '#64748b', background: '#ffffff', border: '1px solid #edf2f7', borderRadius: '18px', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)', fontSize: '14px', lineHeight: '1.6', textAlign: 'center' }}>
              目前還沒有旅伴貼文，成為第一個發起的人吧！
            </div>
          )}

          {!isLoading && travelPicks.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '12px',
              margin: '0 -18px',
              padding: '0 18px 8px',
              overflowX: 'auto',
              overscrollBehaviorX: 'contain',
              scrollPaddingLeft: '18px',
              scrollSnapType: 'x proximity',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>
              {travelPicks.map(post => {
                const countries = post.countries?.length > 0 ? post.countries : (post.country ? [post.country] : []);
                const budgetStyle = BUDGET_STYLE[post.budget_range] || BUDGET_STYLE.standard;
                const imageUrl = post.image_urls?.[0];

                return (
                  <button
                    key={post.id}
                    type="button"
                    onClick={() => navigate(`/travel/${post.id}`)}
                    aria-label={`查看旅伴行程：${post.destination}`}
                    style={{
                      flex: '0 0 clamp(168px, 46vw, 218px)',
                      padding: 0,
                      overflow: 'hidden',
                      color: 'inherit',
                      background: '#ffffff',
                      border: '1px solid rgba(226, 232, 240, 0.9)',
                      borderRadius: '18px',
                      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      scrollSnapAlign: 'start',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: 'clamp(112px, 30vw, 138px)', overflow: 'hidden', background: 'linear-gradient(135deg, #dbeafe 0%, #eef4f9 52%, #d9f3ef 100%)' }}>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MapPin size={28} color="#6a9fd3" strokeWidth={1.8} />
                      </div>
                      {imageUrl && (
                        <img
                          src={getThumbUrl(imageUrl)}
                          onError={(event) => handleImageError(event, imageUrl)}
                          alt=""
                          loading="lazy"
                          style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <div style={{ position: 'absolute', inset: 'auto 0 0', height: '32%', background: 'linear-gradient(180deg, transparent, rgba(15, 23, 42, 0.14))', pointerEvents: 'none' }} />
                    </div>

                    <div style={{ padding: '11px 12px 13px' }}>
                      <div title={post.destination} style={{ marginBottom: '7px', overflow: 'hidden', color: '#0f172a', fontSize: '14px', fontWeight: '800', lineHeight: '1.4', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {post.destination}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px', minHeight: '21px', marginBottom: '8px' }}>
                        {countries[0] && (
                          <span style={{ maxWidth: '84px', padding: '3px 8px', overflow: 'hidden', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '10px', fontWeight: '700', lineHeight: '1.35', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {countries[0]}
                          </span>
                        )}
                        {post.budget_range && (
                          <span style={{ padding: '3px 8px', color: budgetStyle.color, background: budgetStyle.background, borderRadius: '999px', fontSize: '10px', fontWeight: '700', lineHeight: '1.35' }}>
                            {TRAVEL_BUDGET_LABELS[post.budget_range] || post.budget_range}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0, color: '#64748b', fontSize: '10.5px', fontWeight: '500', lineHeight: '1.4' }}>
                        <Calendar size={12} strokeWidth={1.9} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatDateRange(post.trip_start_date, post.trip_end_date)} · 找{post.max_companions || 1}人
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {trendingError && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px', padding: '12px 14px', color: '#9a5b16', background: '#fff8eb', border: '1px solid #f8dfb4', borderRadius: '14px', fontSize: '13px' }}>
            <AlertCircle aria-hidden="true" size={17} />
            <span>{trendingError}</span>
          </div>
        )}

        {(isLoading || popularEvents.length > 0) && <section aria-labelledby="popular-events-title" style={{ marginTop: '25px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '13px' }}>
            <h2 id="popular-events-title" style={{ display: 'flex', alignItems: 'center', gap: '7px', margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '800', letterSpacing: '-0.02em' }}>
              <Flame aria-hidden="true" size={18} color="#e2703a" />
              近期熱門
            </h2>
            <button
              type="button"
              onClick={() => navigate('/events')}
              style={{ padding: '5px 0', color: '#376ba1', background: 'transparent', border: 'none', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
            >
              查看全部
            </button>
          </div>

          {isLoading && (
            <div aria-hidden="true" style={{ height: '264px', borderRadius: '18px', background: 'linear-gradient(110deg, #edf2f7 8%, #f8fafc 18%, #edf2f7 33%)', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }} />
          )}

          {!isLoading && popularEvents.length > 0 && (
            <div style={{ overflow: 'hidden', background: '#ffffff', border: '1px solid rgba(226, 232, 240, 0.85)', borderRadius: '18px', boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)' }}>
              {popularEvents.map((event, index) => {
                const categoryStyle = CATEGORY_STYLE[event.category] || CATEGORY_STYLE.其他;
                const CategoryIcon = categoryStyle.icon;
                const remainingSlots = event.max_participants ? Math.max(event.max_participants - event.approvedCount, 0) : null;

                return (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/events/${event.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/events/${event.id}`); }}
                    style={{
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      minHeight: '76px',
                      padding: '11px 13px',
                      color: 'inherit',
                      background: '#ffffff',
                      border: 'none',
                      borderBottom: index < popularEvents.length - 1 ? '1px solid #f1f5f9' : 'none',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ position: 'relative', flexShrink: 0, width: '64px', height: '52px', overflow: 'hidden', background: categoryStyle.background, borderRadius: '12px' }}>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CategoryIcon size={22} color={categoryStyle.color} strokeWidth={1.9} />
                      </div>
                      {event.image_url && (
                        <img
                          src={event.image_url}
                          onError={(imageEvent) => handleImageError(imageEvent)}
                          alt=""
                          loading="lazy"
                          style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div title={event.title} style={{ overflow: 'hidden', color: '#0f172a', fontSize: '14px', fontWeight: '800', lineHeight: '1.45', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.title}{event.city ? `（${event.city}）` : ''}
                      </div>
                      <div style={{ marginTop: '4px', overflow: 'hidden', color: '#64748b', fontSize: '11.5px', fontWeight: '500', lineHeight: '1.45', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatEventWhen(event.date, event.start_time)}
                        {remainingSlots !== null && ` · 找${remainingSlots}人`}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareEvent(event);
                        }}
                        aria-label="分享"
                        style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '50%', cursor: 'pointer', transition: 'background 0.2s' }}
                      >
                        <Share2 size={16} strokeWidth={2.5} />
                      </button>
                      <ChevronRight size={18} color="#b8c3d1" strokeWidth={2} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>}
      </div>

      <button
        type="button"
        aria-label="發起新活動"
        onClick={() => navigate('/events/create')}
        style={{
          position: 'fixed',
          right: isDesktop ? '20px' : 'max(20px, calc((100vw - 780px) / 2 + 20px))',
          bottom: isDesktop ? '32px' : 'calc(86px + env(safe-area-inset-bottom))',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '54px',
          height: '54px',
          padding: 0,
          color: '#ffffff',
          background: 'linear-gradient(145deg, #5b9bd5 0%, #3e83c8 100%)',
          border: '2px solid rgba(255, 255, 255, 0.92)',
          borderRadius: '50%',
          boxShadow: '0 10px 24px rgba(78, 139, 202, 0.38)',
          cursor: 'pointer'
        }}
      >
        <Plus size={25} strokeWidth={2.2} />
      </button>

      <ShareCardModal 
        isOpen={!!shareEvent} 
        onClose={() => setShareEvent(null)} 
        eventData={shareEvent} 
      />
    </div>
  );
}
